// 予約投稿プレビューの動画を、クリック(またはキーボード)で再生/一時停止するための共通部品。
// 詳細画面(scheduled_post_preview_controller)とフォーム右側(scheduled_post_controller)の両方で使う。
// 動画に重ねた透明なボタンで切り替え、一時停止中だけ中央に再生アイコンを出す。
// 右下のスピーカーボタンで元の動画の音声をオン/オフできる(自動再生ポリシーのため初期状態は常にミュート)。

export const PLAY_TOGGLE_BUTTON_CLASS =
  "absolute inset-0 flex items-center justify-center cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
// 表示の切り替えは外側の hidden だけで行う(hidden と flex を同じ要素に付けると表示がぶつかるため内側に分ける)
export const PLAY_ICON_WRAPPER_CLASS = "hidden"
export const PLAY_ICON_CLASS = "flex w-14 h-14 items-center justify-center rounded-full bg-black/50"

// 一時停止中なら「再生」ボタン(アイコン表示)、再生中なら「一時停止」ボタン(アイコン非表示)
export function playbackToggleState(paused) {
  return paused
    ? { label: "動画を再生", showIcon: true }
    : { label: "動画を一時停止", showIcon: false }
}

// 動画の再生/一時停止を切り替える。自動再生の制限などで play() が拒否されても例外にしない
export function togglePlayback(video) {
  if (!video) return

  if (video.paused) {
    const result = video.play()
    if (result && typeof result.catch === "function") result.catch(() => {})
  } else {
    video.pause()
  }
}

// ボタンの aria-label と再生アイコンの表示を、動画の再生状態に合わせる
export function applyPlaybackToggleState(button, paused) {
  if (!button) return

  const state = playbackToggleState(paused)
  button.setAttribute("aria-label", state.label)
  const icon = button.querySelector("[data-play-icon]")
  if (icon) {
    if (state.showIcon) {
      icon.classList.remove("hidden")
    } else {
      icon.classList.add("hidden")
    }
  }
}

// 生成直後と loadeddata 時に、ボタン表示を動画の実際の再生状態に合わせる。
// 自動再生が拒否されると pause イベントが来ないため、play/pause イベントだけだと「一時停止」表示のまま残る
export function syncInitialPlaybackState(video, button) {
  if (!video || !button) return

  applyPlaybackToggleState(button, video.paused)
  video.addEventListener("loadeddata", () => applyPlaybackToggleState(button, video.paused), { once: true })
}

// innerHTML で組み立てる側(フォーム)向けのボタン HTML。自動再生で始まるので初期状態は「動画を一時停止」
export function playToggleButtonHtml(action) {
  return `<button type="button" class="${PLAY_TOGGLE_BUTTON_CLASS}" aria-label="動画を一時停止" data-play-toggle data-action="${action}">` +
    `<span class="${PLAY_ICON_WRAPPER_CLASS}" data-play-icon><span class="${PLAY_ICON_CLASS}"><span class="iconify lucide--play size-7 text-white"></span></span></span>` +
    `</button>`
}

// スピーカーボタン。再生トグル(inset-0)より DOM 上で後ろに置いて手前にする。
// 右下に置き、カルーセルのドット(下中央)・枚数(右上)・矢印(左右中央)と重ならないようにする
export const SOUND_TOGGLE_BUTTON_CLASS =
  "absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/60 hover:bg-black/70 flex items-center justify-center cursor-pointer tap-target focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
export const SOUND_ICON_MUTED_CLASS = "lucide--volume-x"
export const SOUND_ICON_UNMUTED_CLASS = "lucide--volume-2"

// ミュート中なら「音声をオンにする」、音声オン中なら「音声をオフにする」(aria-pressed は音声オンかどうか)
export function soundToggleState(muted) {
  return muted
    ? { label: "音声をオンにする", pressed: "false", icon: SOUND_ICON_MUTED_CLASS }
    : { label: "音声をオフにする", pressed: "true", icon: SOUND_ICON_UNMUTED_CLASS }
}

// スピーカーボタンと注記の表示を video.muted に合わせる。呼ぶのは volumechange と生成直後だけにし、
// 各経路(ボタン・相手の試聴開始・play() 拒否・再描画前の停止)は video.muted を書き換えるだけにする。
// 表示の情報源を video.muted 1つにすることで「動画はミュートなのにボタンはオン」がどの経路でも起きない。
// 注記は楽曲が選択されているとき(noteEnabled)の音声オン中だけ出す
export function applySoundToggleState(button, note, muted, noteEnabled = true) {
  if (button) {
    const state = soundToggleState(muted)
    button.setAttribute("aria-label", state.label)
    button.setAttribute("aria-pressed", state.pressed)
    const icon = button.querySelector("[data-sound-icon]")
    if (icon) {
      icon.classList.remove(SOUND_ICON_MUTED_CLASS)
      icon.classList.remove(SOUND_ICON_UNMUTED_CLASS)
      icon.classList.add(state.icon)
    }
  }

  if (note) {
    if (muted || !noteEnabled) {
      note.classList.add("hidden")
    } else {
      note.classList.remove("hidden")
    }
  }
}

// 元の動画の音声をオン/オフする。オンにしたら true を返す(呼び出し側が他の試聴を止めるため)。
// 一時停止中にオンにしたら再生も始める。play() が拒否されたらミュートに戻す(表示は volumechange で戻る)。
// 音量は触らない(既定の 1 のまま)。楽曲なしの投稿は元の音声がそのまま使われるため、音量バランスの確認は音楽欄の試聴に任せる
export function toggleSound(video) {
  if (!video) return false

  if (!video.muted) {
    video.muted = true
    return false
  }

  video.muted = false
  if (video.paused) {
    const result = video.play()
    if (result && typeof result.catch === "function") {
      result.catch(() => { video.muted = true })
    }
  }
  return true
}

// 再描画・離脱の前に、差し替える動画の音を確実に止める
export function stopPreviewVideo(video) {
  if (!video) return

  video.pause()
  video.muted = true
}

// innerHTML で組み立てる側(フォーム)向けのスピーカーボタン HTML。初期状態はミュート
export function soundToggleButtonHtml(action) {
  return `<button type="button" class="${SOUND_TOGGLE_BUTTON_CLASS}" aria-label="音声をオンにする" aria-pressed="false" data-sound-toggle data-action="${action}">` +
    `<span class="iconify ${SOUND_ICON_MUTED_CLASS} size-5 text-white" data-sound-icon></span>` +
    `</button>`
}
